import {getUserDefinedObjectAdditionalColumnDetails} from "~/domainModels/userDefinedObjectAdditionalColumns";
import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";

import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import type {BtlActivity} from "~/domainModels/btlActivities";
import type {ProductCatalogue} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/productCataloguesWorkspaceCapability/domain/ProductCatalogue";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import type {OrganizationalUnit} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import type {EventContainerTemplate as JourneysModuleConfiguration} from "~/domainModels/workspaceModules/eventContainersModule";
import {
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID,
    normalizeEmotoradCustomerZone,
} from "~/domainModels/workspaceSpecific/emotorad";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import {getGeographyDetailsByPincode} from "~/utilities/apis/geography";

function resolveEmotoradCustomerPincodeTextForGeographyLookup(getAdditionalColumns: CapabilityUdoColumnResolver,
    journey: EventContainerWithEvents,
    journeysModuleConfiguration: JourneysModuleConfiguration,
    users: Array<User>,
    userTemplates: Array<UserTemplate>,
    organizationalUnits: Array<OrganizationalUnit>,
    organizationalUnitTemplates: Array<OrganizationalUnitTemplate>,
    products: Array<ProductCatalogue>,
): string {
    const raw = journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID];
    if (raw === null || raw === undefined) {
        return "";
    }
    const pincodeColumn = getAdditionalColumns(journeysModuleConfiguration).find(
        (column) => column.id === EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    );
    if (pincodeColumn === undefined) {
        return String(raw).trim();
    }
    const resolved = getUserDefinedObjectAdditionalColumnDetails(pincodeColumn).stringValueGetter({
        value: raw as never,
        users,
        userTemplates,
        organizationalUnits,
        organizationalUnitTemplates,
        products,
    });
    if (resolved !== null && resolved !== undefined && String(resolved).trim() !== "" && resolved !== "?") {
        return String(resolved).trim();
    }
    return String(raw).trim();
}

/** Lifecycle Overview table - resolve customer zone label (select options + normalization). */
export function formatEmotoradLifecycleCustomerZoneDisplay(getAdditionalColumns: CapabilityUdoColumnResolver,
    journey: EventContainerWithEvents,
    journeysModuleConfiguration: JourneysModuleConfiguration,
    users: Array<User>,
    userTemplates: Array<UserTemplate>,
    organizationalUnits: Array<OrganizationalUnit>,
    organizationalUnitTemplates: Array<OrganizationalUnitTemplate>,
    products: Array<ProductCatalogue>,
): string {
    const raw = journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID];
    if (raw === null || raw === undefined || String(raw).trim() === "") {
        return "";
    }
    const zoneColumn = getAdditionalColumns(journeysModuleConfiguration).find(
        (column) => column.id === EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID,
    );
    if (zoneColumn === undefined) {
        return normalizeEmotoradCustomerZone(String(raw));
    }
    const resolved = getUserDefinedObjectAdditionalColumnDetails(zoneColumn).stringValueGetter({
        value: raw as never,
        users,
        userTemplates,
        organizationalUnits,
        organizationalUnitTemplates,
        products,
    });
    if (resolved === null || resolved === undefined || String(resolved).trim() === "" || resolved === "?") {
        return normalizeEmotoradCustomerZone(String(raw));
    }
    return normalizeEmotoradCustomerZone(String(resolved));
}

/**
 * Emotorad journeys often omit CUSTOMER_ZONE on create; derive from customer pincode when missing
 * (same geography API as lead/call flows).
 */
export async function augmentEmotoradLifecycleCustomerZoneFromPincode(getAdditionalColumns: CapabilityUdoColumnResolver,
    journeys: Array<EventContainerWithEvents>,
    args: {
        intellsysBaseUrl: string;
        accessToken: string;
        journeysModuleConfiguration: JourneysModuleConfiguration;
        eventTemplates: Array<OttopilotEventTemplate>;
        users: Array<User>;
        userTemplates: Array<UserTemplate>;
        organizationalUnits: Array<OrganizationalUnit>;
        organizationalUnitTemplates: Array<OrganizationalUnitTemplate>;
        btlActivities: Array<BtlActivity>;
        products: Array<ProductCatalogue>;
    },
): Promise<Array<EventContainerWithEvents>> {
    const pincodeToZoneId = new Map<string, Uuid | null>();

    return Promise.all(
        journeys.map(async (journey) => {
            const existingZone = journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID];
            if (existingZone !== null && existingZone !== undefined && String(existingZone).trim() !== "") {
                return journey;
            }

            const pincodeText = resolveEmotoradCustomerPincodeTextForGeographyLookup(
                getAdditionalColumns,
                journey,
                args.journeysModuleConfiguration,
                args.users,
                args.userTemplates,
                args.organizationalUnits,
                args.organizationalUnitTemplates,
                args.products,
            );
            if (pincodeText === "") {
                return journey;
            }

            let zoneId = pincodeToZoneId.get(pincodeText);
            if (zoneId === undefined) {
                try {
                    const details = await getGeographyDetailsByPincode({
                        intellsysBaseUrl: args.intellsysBaseUrl,
                        accessToken: args.accessToken,
                        pincode: pincodeText,
                        dialCode: "+91",
                    });
                    zoneId = details.zone;
                } catch {
                    zoneId = null;
                }
                pincodeToZoneId.set(pincodeText, zoneId);
            }

            if (zoneId === null) {
                return journey;
            }

            return {
                ...journey,
                additionalColumnValues: {
                    ...journey.additionalColumnValues,
                    [EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID]: zoneId,
                },
            };
        }),
    );
}
